import {savedDay, safeURL, plainText} from './api-data.js';

const DAY = 86400000;
const sources = ['cwa', 'open-meteo'];
const timestamp = value => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? Date.parse(value) : NaN;
const adjacentDay = (now, offset) => savedDay(new Date(now + offset * DAY).toISOString());
const httpsURL = value => { const url = safeURL(value); return url?.startsWith('https:') ? url : null; };

export function currentWeather(snapshot, now = Date.now()) {
    const current = snapshot?.current;
    return current && current.dataTime <= now + 300000 && now < current.validUntil ? current : null;
}

export function weatherSnapshot(response, previous, now = Date.now()) {
    if (!['ready', 'degraded', 'stale'].includes(response?.status) || response.location?.id !== 'taipei' || response.location?.timezone !== 'Asia/Taipei') throw new Error('Invalid Taipei weather response');
    const raw = response.current;
    const dataTime = timestamp(raw?.data_time), validUntil = timestamp(raw?.valid_until);
    const current = raw && sources.includes(raw.source) && raw.data_type === (raw.source === 'cwa' ? 'observation' : 'model') &&
        Number.isFinite(raw.temperature_c) && raw.temperature_c >= -50 && raw.temperature_c <= 60 &&
        Number.isFinite(dataTime) && Number.isFinite(validUntil) && dataTime <= now + 300000 && validUntil > dataTime && validUntil > now &&
        now < dataTime + 7200000 ? {
            source: raw.source, type: raw.data_type, dataTime,
            validUntil: Math.min(validUntil, dataTime + 7200000), temperature: raw.temperature_c,
            code: plainText(raw.condition_code) || 'unknown'
        } : null;
    let sun = null;
    if (sources.includes(response.sun?.source) && Array.isArray(response.sun.days)) {
        const today = savedDay(new Date(now).toISOString()), tomorrow = adjacentDay(now, 1);
        const byDate = new Map();
        for (const day of response.sun.days) {
            const rise = timestamp(day?.sunrise_at), set = timestamp(day?.sunset_at);
            if (![today, tomorrow].includes(day?.date) || !Number.isFinite(rise) || !Number.isFinite(set) || rise >= set ||
                savedDay(new Date(rise).toISOString()) !== day.date || savedDay(new Date(set).toISOString()) !== day.date) continue;
            byDate.set(day.date, {date: day.date, rise, set});
        }
        const todaySun = byDate.get(today);
        const previousSet = timestamp(response.sun.previous_sunset_at);
        if (todaySun && Number.isFinite(previousSet) && previousSet < todaySun.rise &&
            savedDay(new Date(previousSet).toISOString()) === adjacentDay(now, -1)) {
            todaySun.previousSet = previousSet;
        }
        // Retain only a real preceding sunset already seen on this page, from the same source.
        // It anchors the moon across midnight without persisting the no-store API response.
        if (previous?.sun?.source === response.sun.source) {
            const yesterday = previous.sun.days.find(day => day.date === adjacentDay(now, -1));
            if (yesterday) byDate.set(yesterday.date, yesterday);
        }
        if (byDate.size) sun = {source: response.sun.source, days: [...byDate.values()].sort((a, b) => a.rise - b.rise)};
    }
    const translations = {};
    for (const locale of ['en-US', 'zh-TW']) {
        const copy = response.translations?.[locale];
        translations[locale] = {location: plainText(copy?.location).slice(0, 40), condition: plainText(copy?.condition).slice(0, 80)};
    }
    const attribution = Array.isArray(response.attribution) ? response.attribution.filter(item => sources.includes(item?.source) && httpsURL(item.url)).map(item => ({
        source: item.source, name: plainText(item.name), url: httpsURL(item.url), licenseURL: httpsURL(item.license_url),
        datasets: Array.isArray(item.datasets) ? item.datasets.filter(value => typeof value === 'string').slice(0, 4) : []
    })) : [];
    return {status: response.status, current, sun, translations, attribution};
}

export function solarState(days, now = Date.now()) {
    if (!Number.isFinite(now) || !Array.isArray(days)) return null;
    const today = days.find(day => day.date === savedDay(new Date(now).toISOString()));
    if (!today) return null;
    const {rise, set} = today;
    if (now < rise) {
        const previousSet = today.previousSet ?? days.find(day => day.date === adjacentDay(now, -1))?.set;
        const progress = Number.isFinite(previousSet) && rise > previousSet ? Math.max(0, Math.min(1, (now - previousSet) / (rise - previousSet))) : null;
        return {day: false, until: Math.ceil((rise - now) / 60000), event: 'sunrise', progress};
    }
    if (now < set) return {day: true, until: Math.ceil((set - now) / 60000), event: 'sunset', progress: (now - rise) / (set - rise)};
    const next = days.find(day => day.date === adjacentDay(now, 1));
    return {day: false, until: next ? Math.ceil((next.rise - now) / 60000) : null, event: next ? 'sunrise' : null, progress: next ? (now - set) / (next.rise - set) : null};
}

export function solarPosition(progress) {
    // Keep a decorative moon centered only when the night interval is unavailable.
    const phase = Number.isFinite(progress) ? Math.max(0, Math.min(1, progress)) : .5;
    return {left: phase * 100, top: 12};
}
